import {
  useMutation,
  UseMutationResult,
  useQuery,
} from "@tanstack/react-query";
import { queryKeys } from "../constants";
import {
  createProductTransfer,
  deleteProductTransfer,
  getProductTransferById,
  getProductTransfers,
  updateProductTransfer,
  updateProductTransferStatus,
} from "services";
import { handleAPIError, queryClient } from "utils";
import { ProductTransferType } from "types";
import { toast } from "react-toastify";

// Fetch all product transfers
export const useGetProductTransfers = (id?: string | number) => {
  return useQuery({
    queryKey: [queryKeys.productTransfer, id],
    queryFn: async () => await getProductTransfers(id),
  });
};

// Fetch a single product transfer by ID
export const useGetProductTransferById = (id: string) => {
  return useQuery({
    queryKey: [queryKeys.productTransfer, id], // Separate key for fetching single product
    queryFn: async () => await getProductTransferById(id),
    enabled: !!id, // Only run query if ID is provided
  });
};

// Create a new product transfer
export const useCreateProductTransfer = () => {
  return useMutation({
    mutationFn: createProductTransfer,
    onSuccess: async (data) => {
      toast.success("Product transfer created successfully");
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.productTransfer],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

type UpdateProductTransferPayload = {
  id: string;
  data: ProductTransferType;
};

// Update product transfer hook
export const useUpdateProductTransfer = (): UseMutationResult<
  any,
  Error,
  UpdateProductTransferPayload
> => {
  return useMutation<any, Error, UpdateProductTransferPayload>({
    mutationFn: ({ id, data }: UpdateProductTransferPayload) =>
      updateProductTransfer(id, data),
    onSuccess: async () => {
      toast.success("Product transfer updated successfully");
      // Invalidate product transfer query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.productTransfer],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

type UpdateStatusPayload = {
  id: string;
  data: { status: string };
};

// / Update product transfer status hook
export const useUpdateProductTransferStatus = (): UseMutationResult<
  any,
  Error,
  UpdateStatusPayload
> => {
  return useMutation<any, Error, UpdateStatusPayload>({
    mutationFn: ({ id, data }: UpdateStatusPayload) =>
      updateProductTransferStatus(id, data),
    onSuccess: async () => {
      // Invalidate product transfer query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.productTransfer],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Delete a product transfer
export const useDeleteProductTransfer = () => {
  return useMutation({
    mutationFn: deleteProductTransfer,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.productTransfer],
      });

      toast.success("Product transfer deleted successfully.");
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};
